import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Brain, Search } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import {
    getMemoryStats,
    listMemoryConversations,
    searchMemory,
    type MemoryConversation,
    type MemoryHit,
} from "@/api/memory";
import { formatRelativeTime } from "@/lib/utils";

export function MemoryPage(): React.ReactNode {
    const stats = useQuery({ queryKey: ["memory", "stats"], queryFn: getMemoryStats });
    const conversations = useQuery({
        queryKey: ["memory", "conversations"],
        queryFn: () => listMemoryConversations(),
    });

    const [query, setQuery] = React.useState("");
    const [debounced, setDebounced] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    const search = useQuery({
        queryKey: ["memory", "search", debounced],
        queryFn: () => searchMemory(debounced),
        enabled: debounced.length >= 2,
    });

    if (stats.data && !stats.data.enabled) {
        return (
            <>
                <PageHeader title="Memory" description="Ported from cmem — hybrid retrieval over past constructor sessions." />
                <div className="px-6 py-10">
                    <EmptyState
                        icon={<Brain className="h-8 w-8" />}
                        title="Memory is disabled"
                        description="Set CONSTRUCTOR_MEMORY_ENABLED=true and provide a memory database to enable this view."
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Memory"
                description="Hybrid (vector + lexical) recall over past constructor sessions and saved facts."
            />
            <div className="space-y-6 px-6 py-4">
                {/* Stats row */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <StatCard label="Conversations" value={stats.data?.conversations ?? "—"} />
                    <StatCard label="Messages" value={stats.data?.messages ?? "—"} />
                    <StatCard label="Embedded" value={stats.data?.messageEmbeddings ?? "—"} />
                    <StatCard label="Knowledge nodes" value={stats.data?.knowledgeNodes ?? "—"} />
                    <StatCard label="Embedding model" value={stats.data?.embeddingModel ?? "—"} small />
                </div>

                {/* Search */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search memory (vector + tsvector RRF fusion)…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="memory-search"
                        />
                    </div>
                    {debounced.length >= 2 && (
                        <div data-testid="memory-search-results">
                            {search.isLoading && <Spinner />}
                            {!search.isLoading && (search.data?.hits.length ?? 0) === 0 && (
                                <div className="text-sm text-muted-foreground">No matches.</div>
                            )}
                            {(search.data?.hits ?? []).length > 0 && (
                                <ul className="space-y-2">
                                    {search.data!.hits.map((h) => <HitRow key={`${h.kind}-${h.id}`} hit={h} />)}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                {/* Conversations */}
                <div>
                    <h2 className="mb-3 text-sm font-semibold">Recent conversations</h2>
                    {conversations.isLoading && <Spinner />}
                    {!conversations.isLoading && (conversations.data?.length ?? 0) === 0 && (
                        <EmptyState
                            icon={<Brain className="h-8 w-8" />}
                            title="No memory yet"
                            description="Open a constructor session and chat — turns are recorded here automatically."
                        />
                    )}
                    {(conversations.data?.length ?? 0) > 0 && (
                        <ul className="space-y-1.5">
                            {conversations.data!.map((c) => <ConversationRow key={c.id} c={c} />)}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );
}

function StatCard({ label, value, small }: { label: string; value: string | number; small?: boolean }): React.ReactNode {
    return (
        <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={small ? "mt-1 truncate text-xs font-mono" : "mt-1 text-2xl font-semibold tabular-nums"}>{value}</div>
        </div>
    );
}

function ConversationRow({ c }: { c: MemoryConversation }): React.ReactNode {
    const href = scopeHref(c.scopeKind, c.scopeRef);
    const scopeEl = (
        <span className="font-mono text-xs text-muted-foreground">{c.scopeKind}:{c.scopeRef}</span>
    );
    return (
        <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
            <div>
                {href ? <a href={href} className="hover:underline">{scopeEl}</a> : scopeEl}
                <span className="ml-2">{c.title || `conversation #${c.id}`}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{c.messageCount} msg</span>
                <span>{formatRelativeTime(c.lastActivityAt)}</span>
            </div>
        </li>
    );
}

function HitRow({ hit }: { hit: MemoryHit }): React.ReactNode {
    // Raw scores moved to tooltip — they dominated the content visually.
    const scoreTooltip = `vec ${hit.vectorScore.toFixed(3)} · rrf ${hit.rrfScore.toFixed(4)}`;
    const href = scopeHref(hit.scopeKind, hit.scopeRef);
    const scope = (
        <span className="font-mono">{hit.scopeKind}:{hit.scopeRef}</span>
    );
    return (
        <li className="rounded-md border border-border bg-card p-3 text-sm">
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground" title={scoreTooltip}>
                <span className="rounded-full bg-muted px-2 py-0.5 font-mono">{hit.kind} · {hit.role}</span>
                {href ? <a href={href} className="hover:underline">{scope}</a> : scope}
            </div>
            <div className="text-foreground">{hit.snippet || hit.content?.slice(0, 240)}</div>
        </li>
    );
}

function scopeHref(kind: string, ref: string): string | null {
    if (kind === "draft" && ref) return `/constructor/draft/${ref}`;
    if (kind === "page" && ref) return `/pages/${ref}`;
    return null;
}
